/**
 * Hook useDealFilterOptions - Opções para filtros do Kanban de Deals
 * Busca todas as opções via RPC unificada get_deal_filter_options
 * Versão: 2.0.0 - Otimizado: 4 queries → 1 RPC
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger para depuração
const logger = createLogger('DealFilterOptions');

// Interfaces para tipagem do retorno
interface Seller {
  id: string;
  name: string;
  email: string | null;
  avatar_url: string | null;
}

interface Category {
  id: string;
  name: string;
  parent_id: string | null;
}

interface Product {
  id: string;
  name: string;
  sku: string | null;
  category_id: string | null;
}

interface FilterOptionsResponse {
  sellers: Seller[];
  sources: string[];
  categories: Category[];
  products: Product[];
}

/**
 * Hook para buscar opções dos filtros do Kanban de Deals
 * Usa RPC unificada para reduzir requisições (4 → 1)
 */
export function useDealFilterOptions() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  // Query unificada via RPC get_deal_filter_options
  const optionsQuery = useQuery({
    queryKey: ['deal-filter-options', tenantId],
    queryFn: async () => {
      if (!tenantId) return null;
      
      logger.debug('Buscando opções de filtro via RPC unificada');
      
      const { data, error } = await supabase.rpc('get_deal_filter_options');

      if (error) {
        logger.error('Erro ao buscar opções de filtro:', error);
        throw error;
      }

      // Cast para tipo correto (RPC retorna JSONB)
      const result = data as unknown as FilterOptionsResponse | null;

      logger.debug('Opções de filtro carregadas:', {
        sellers: (result?.sellers || []).length,
        sources: (result?.sources || []).length,
        categories: (result?.categories || []).length,
        products: (result?.products || []).length,
      });

      return result;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutos de cache
  });

  // Rede de segurança: deduplica origens por lower() para proteger contra
  // dados legados em caixa mista (ex: "Facebook" vs "facebook") que ainda
  // estejam em caches antigos da RPC. A migration + RPC já cobrem o caso,
  // esta camada é uma proteção adicional inofensiva.
  const dedupedSources = Array.from(
    new Set((optionsQuery.data?.sources || []).map((s) => s.toLowerCase()))
  ).sort();

  return {
    sellers: optionsQuery.data?.sellers || [],
    sources: dedupedSources,
    categories: optionsQuery.data?.categories || [],
    products: optionsQuery.data?.products || [],
    // Mantém users vazio (não usado atualmente, pode ser adicionado à RPC se necessário)
    users: [],
    isLoading: optionsQuery.isLoading,
  };
}
